/**
 * Maps Core - Simple Google Maps for Embark Map List
 *
 * Provides a simple city overview map functionality for embark-map-list sections.
 * Automatically initializes when a map tab is clicked.
 */

class EmbarkMaps {
  constructor() {
    this.apiKey = "AIzaSyAFJQWg5a527xfXbxOeaN7GOGVR99UM0vU";
    this.apiLoaded = false;
    this.apiLoading = false;
    this.initializedMaps = new Set();

    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      // Find all embark map sections and set up click handlers
      const mapTabs = document.querySelectorAll('[id^="tab-map"]');

      mapTabs.forEach(mapTab => {
        mapTab.addEventListener('click', () => {
          const mapId = 'locations-map';

          // Scroll to map on mobile when tab is clicked
          this.scrollToMapOnMobile();

          // Check if already initialized
          if (this.initializedMaps.has(mapId)) return;

          // Mark as loading to prevent double initialization
          this.initializedMaps.add(mapId);

          this.createCityMap(mapId);
        });
      });
    });
  }

  async createCityMap(mapId) {
    try {
      // Load Google Maps API if needed
      await this.loadGoogleMapsAPI();

      // Get location data from the embedded JSON (same locations as displayed in the list)
      const dataScript = document.querySelector('script.locations_data');

      if (!dataScript) {
        console.error('Embark Maps: No location data found for', mapId);
        this.showError(mapId, 'No location data found');
        return;
      }

      let locations;
      try {
        const jsonText = dataScript.textContent.trim();
        // The script contains an array of location objects, so wrap in array brackets
        const locationsArray = `[${jsonText}]`;
        locations = JSON.parse(locationsArray);
      } catch (error) {
        console.error('Embark Maps: Failed to parse location data:', error);
        this.showError(mapId, 'Invalid location data');
        return;
      }

      if (locations.length === 0) {
        console.error('Embark Maps: No locations found for', mapId);
        this.showError(mapId, 'No locations found');
        return;
      }



      // Create the map
      const container = document.getElementById(mapId);
      if (!container) {
        console.error('Embark Maps: Container not found for', mapId);
        return;
      }

      // Show loading state
      container.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666;">Loading map...</div>';

      const map = new google.maps.Map(container, {
        mapId: "5d278bcd39e22421",
        zoom: 15,
        center: locations[0].location,
        clickableIcons: false,
        disableDefaultUI: true,
        gestureHandling: 'greedy',
        fullscreenControl: true,
        cameraControl: true,
        scaleControl: true
      });

      // Add markers and bounds
      const bounds = new google.maps.LatLngBounds();

      locations.forEach(location => {
        this.createMarker(map, location);
        bounds.extend(location.location);
      });

      // Fits map to bounds only if more than one location
      if (locations.length > 1) {
        map.fitBounds(bounds);
      }

    } catch (error) {
      console.error('Embark Maps: Failed to create map for', mapId, error);
      this.showError(mapId, 'Failed to load map');
      // Remove from initialized set so user can try again
      this.initializedMaps.delete(mapId);
    }
  }

  createMarker(map, location) {
    // Create marker content
    const markerContent = document.createElement("div");
    markerContent.className = "tg-marker";
    markerContent.innerHTML = `
      <div class="marker-label">
        ${location.name}
        <p>View Details</p>
      </div>
      <div class="relative">
        <span class="marker-tap-area"></span>
        <svg class="custom-marker__icon" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000">
            <path d="M12 2c-4.2 0-8 3.22-8 8.2 0 3.18 2.45 6.92 7.34 11.23.38.33.95.33 1.33 0C17.55 17.12 20 13.38 20 10.2 20 5.22 16.2 2 12 2zm0 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
        </svg>
      </div>
    `;

    // Create the marker with better default marker suppression
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: location.location,
      content: markerContent,
      title: location.name,
      gmpClickable: false,  // Disable default click behavior
      zIndex: 1000  // Ensure our marker is above default markers
    });

    // Force hide any default marker elements that might appear
    setTimeout(() => {
      const markerWrapper = markerContent.closest('gmp-advanced-marker');
      if (markerWrapper) {
        markerWrapper.style.zIndex = '1000';
        // Hide any default marker pin that might show
        const defaultPins = markerWrapper.querySelectorAll('[data-marker-type="pin"]');
        defaultPins.forEach(pin => pin.style.display = 'none');
      }
    }, 100);

    // Handle mobile vs desktop behavior - check for touch capability
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isMobile) {
      // Mobile: First tap shows label, second tap (on label) goes to page

      // Single event listener on marker wrapper for reliability
      const handleMarkerClick = (e) => {
        // Check if click is on the label - if so, let it navigate
        const markerLabel = markerContent.querySelector('.marker-label');
        if (markerLabel && (markerLabel.contains(e.target) || e.target === markerLabel)) {
          // Click is on label - navigate to location
          if (location.url) {
            e.stopPropagation();
            window.location.href = location.url;
            return;
          }
        }

        e.preventDefault();
        e.stopPropagation();

        console.log('Marker clicked for:', location.name); // Temporary debug

        // Check if this marker is already active
        const markerWrapper = markerContent.closest('gmp-advanced-marker');
        const isCurrentlyActive = markerWrapper && markerWrapper.classList.contains('marker-active');

        if (isCurrentlyActive) {
          // Second tap - hide the label
          markerWrapper.classList.remove('marker-active');
        } else {
          // First tap - show the label and center map
          this.activateMarker(markerContent, map, location);
          // Scroll to map on mobile when marker is tapped
          this.scrollToMapOnMobile();
        }
      };

      // Listen on the entire marker wrapper (covers tap area, SVG, and everything)
      markerContent.addEventListener('click', handleMarkerClick);
      markerContent.addEventListener('touchend', handleMarkerClick);

    } else {
      // Desktop: Hover shows label, click goes to location page

      // Hover behavior using same marker-active class
      markerContent.addEventListener('mouseenter', () => {
        // Activate this marker (no centering for hover)
        this.activateMarker(markerContent);
      });

      markerContent.addEventListener('mouseleave', () => {
        const markerWrapper = markerContent.closest('gmp-advanced-marker');
        if (markerWrapper) {
          markerWrapper.classList.remove('marker-active');
        }
      });

      // Click goes directly to location page
      if (location.url) {
        markerContent.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = location.url;
        });
      }
    }

    return marker;
  }

  async loadGoogleMapsAPI() {
    if (this.apiLoaded) return Promise.resolve();
    if (this.apiLoading) return this.apiLoadingPromise;

    this.apiLoading = true;

    this.apiLoadingPromise = new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        this.apiLoaded = true;
        this.apiLoading = false;
        resolve();
        return;
      }

      window.initEmbarkMaps = () => {
        this.apiLoaded = true;
        this.apiLoading = false;
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&loading=async&callback=initEmbarkMaps&v=weekly&libraries=marker`;
      script.async = true;
      script.onerror = () => {
        this.apiLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      }
      document.head.appendChild(script);
    });

    return this.apiLoadingPromise;
  }



  // Helper method to clear all active markers
  clearActiveMarkers() {
    const allMarkerWrappers = document.querySelectorAll('gmp-advanced-marker.marker-active');
    allMarkerWrappers.forEach(wrapper => {
      wrapper.classList.remove('marker-active');
    });
  }

  // Helper method to activate a marker
  activateMarker(markerContent, map, location) {
    this.clearActiveMarkers();
    const markerWrapper = markerContent.closest('gmp-advanced-marker');
    if (markerWrapper) {
      markerWrapper.classList.add('marker-active');
      if (map && location) {
        map.panTo(location.location);
      }
    }
  }

  // Helper method to scroll map into view on mobile
  scrollToMapOnMobile() {
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isMobile) return;

    const mapContainer = document.querySelector('.map-container');
    if (mapContainer) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        mapContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }

  showError(mapId, message) {
    const container = document.getElementById(mapId);
    if (container) {
      container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666;">${message}</div>`;
    }
  }
}

// Initialize the embark maps system
new EmbarkMaps();